import { iForumEntity } from "@/interfaces/forum";
import { UserModel } from "@/models";
import { iUser } from "@/interfaces/users";

(function (angular) {
  angular
    .module("app")
    .directive("forumEditor", forumEditor)
    .directive("forumEditorInput", forumEditorInput)
    .controller("forumEditorInputCtrl", forumEditorInputCtrl)
    .directive("forumEditorAllowMentions", forumEditorAllowMentions);

  forumEditor.$inject = ["$compile", "$timeout"];

  function forumEditor($compile, $timeout) {
    /**
     * Function which applies the features to the child forumEditorInput
     * directive, which handles the input of data.
     */
    function link($scope, $element) {
      const directiveMapping = {
        "allow-mentions": "forum-editor-allow-mentions",
      };
      let needsRecompile = false;

      $scope.completions = { items: [], active: null };
      $scope.features = $scope.features || [];
      $scope.entities = $scope.entities || [];

      /**
       * Check if the forum-input directive includes a directive.
       *
       * @param  {string} directiveName
       * @return {boolean}
       */
      function hasDirective(directiveName: string) {
        return $element.find(".fe-input").attr(directiveName);
      }

      /**
       * Check if we're enabling a feature.
       *
       * @param  {string} featureName
       * @return {boolean}
       */
      function hasFeature(featureName: string) {
        return $scope.features.indexOf(featureName) !== -1;
      }

      // Add an attribute to the DOM element for each feature
      Object.keys(directiveMapping).forEach((feature) => {
        const directive = directiveMapping[feature];

        if (hasFeature(feature) && !hasDirective(directive)) {
          $element.find(".fe-input").attr(directive, "");
          needsRecompile = true;
        }
      });

      if (needsRecompile) {
        // Re-compile the features of the forum editor, to instantiate the
        // directives
        const input = $compile($element.find(".fe-input").clone())($scope);
        $timeout(() => {
          $scope.$digest();
          $element.find(".fe-input").replaceWith(input);
        }, 0);
      }
    }

    return {
      restrict: "E",
      transclude: true,
      templateUrl: "directives/forumEditor.html",
      link,
      scope: {
        ngModel: "=",
        ngFocus: "&",
        entities: "=",
        features: "=?",
        placeholder: "@",
      },
    };
  }

  /**
   * Base controller-directive to provide an editor which allows for entities
   * in the editor to be included with a simple '@' character. This would
   * allow for events, locations, etc to be included in the future.
   *
   * @return {Object}
   */
  function forumEditorInput() {
    return {
      require: "ngModel",
      restrict: "A",
      controller: "forumEditorInputCtrl",
      controllerAs: "vm",
      bindToController: true,
      link: ($scope, $element, $attrs, ngModel) => {
        $scope.vm.init(ngModel);
      },
      scope: {
        entities: "=",
      },
    };
  }

  forumEditorInputCtrl.$inject = ["$element", "$q", "$scope", "$timeout"];

  function forumEditorInputCtrl($element, $q, $scope, $timeout) {
    const vm = this;
    const $tempElement = document.createElement("span");

    $scope.$parent.completions = { items: [], active: null };
    $scope.$parent.searching = false;

    vm.encoders = {};
    vm.decoders = {};
    vm.highlighters = {};

    vm.completionFunctions = [];
    vm.ngModel = null;
    vm.moved = false;
    vm.searchRegex = new RegExp("@(\\w+(?: \\w+)?)$");

    vm.autogrow = autogrow;
    vm.end = end;
    vm.entities = vm.entities === undefined ? [] : vm.entities;
    vm.formatters = [];
    vm.init = init;
    vm.registerCompletion = registerCompletion;
    vm.registerFormatter = registerFormatter;
    vm.registerParser = registerParser;
    vm.registerDecoder = registerDecoder;
    vm.registerHighlighter = registerHighlighter;
    vm.registerEncoder = registerEncoder;
    vm.render = render;
    vm.parsers = [];
    vm.start = start;
    vm.up = up;
    vm.down = down;

    vm.select = select;
    // Bit naughty, however, it works.
    $scope.$parent.select = select;

    /**
     * Initialise the forum editor directive. Uses angular $formatters,
     * $parsers and the $render internal methods.
     *
     * @return {void}
     */
    function init(model) {
      $scope.$parent.select = vm.select;

      vm.ngModel = model;

      vm.formatters.map((item) => {
        vm.ngModel.$formatters.push(item);
      });

      vm.parsers.map((item) => {
        vm.ngModel.$parsers.push(item);
      });

      // Make the textarea render, as well as the "highlighter" element.
      vm.ngModel.$render = function () {
        $element.val(vm.ngModel.$viewValue || "");
        vm.render();
      };
    }

    /**
     * Register an encoder for a particular type.
     *
     * @param  {String]}  type
     * @param  {Function} func
     * @return {void}
     */
    function registerEncoder(type, func) {
      vm.encoders[type] = func;
      vm.render();
    }

    /**
     * Register an decoder for a particular type.
     *
     * @param  {String]}  type
     * @param  {Function} func
     * @return {void}
     */
    function registerDecoder(type, func) {
      vm.decoders[type] = func;
      vm.render();
    }

    /**
     * Register a formatter for the vm.ngModel.
     *
     * @return {String}
     */
    function registerHighlighter(type, func) {
      vm.highlighters[type] = func;
      vm.render();
    }

    /**
     * Register a function which returns a Promise with all of the data in
     * an array.
     *
     * @return {Promise}
     */
    function registerCompletion(func: () => Promise<any[]>) {
      vm.completionFunctions.push(func);
    }

    /**
     * Register a formatter for the vm.ngModel.
     *
     * @return {String}
     */
    function registerFormatter(func) {
      vm.formatters.push(func);
      vm.ngModel.$formatters.push(func);
    }

    /**
     * Register a parser for the vm.ngModel.
     *
     * @return {String}
     */
    function registerParser(func) {
      vm.parsers.push(func);
      vm.ngModel.$parsers.push(func);
    }

    /**
     * Render the textarea base on the value of vm.ngModel.
     *
     * @return {String}
     */
    function render() {
      let value = (vm.ngModel.$modelValue || "").toString();

      try {
        // Make the browser do HTML stuff for us.
        $tempElement.textContent = value;
        value = $tempElement.innerHTML;
      } finally {
        // Clean up.
        $tempElement.textContent = null;
      }

      vm.entities.forEach((entity: iForumEntity, index: number) => {
        if (vm.decoders && vm.decoders[entity.entity_type]) {
          value = value.replace(
            new RegExp("@\\[" + index + "\\]"),
            vm.decoders[entity.entity_type](index),
          );
        }
      });

      value = value.replace(
        vm.searchRegex,
        '<span class="fe-dropdown-position">@$1</span>',
      );
      $element.next(".fe-highlight").html(value);
      return value;
    }

    /**
     * Start the process of tagging someone in the forum post.
     *
     * @param {RegExpMatchArray} match
     * @return {void}
     */
    function start(match: RegExpMatchArray) {
      $scope.$parent.searching = true;
      $element
        .parent()
        .find(".fe-dropdown")
        .css({
          position: "absolute",
          top:
            $element.next(".fe-highlight").find(".fe-dropdown-position")[0]
              .offsetTop + 25,
          left: $element.next(".fe-highlight").find(".fe-dropdown-position")[0]
            .offsetLeft,
        });

      const promises = vm.completionFunctions.map((func) => func(match[1]));

      Promise.all(promises).then((resultSets: any[]) => {
        $scope.$parent.completions.items = [];
        $scope.$parent.completions.active = null;

        resultSets.forEach((resultSet) => {
          resultSet.forEach((result) => {
            $scope.$parent.completions.items.push(result);
          });
        });

        $scope.$parent.completions.active = $scope.$parent.completions.items[0];
      });
    }

    /**
     * End the process of tagging someone in the forum post.
     *
     * @return {void}
     */
    function end() {
      $scope.$parent.searching = false;
      $scope.$parent.completions.items = [];
      $scope.$parent.completions.active = null;
    }

    /**
     * Automatically grow the text box to the desired height since the box will scroll with up/down arrow selection.
     * @return {[type]} [description]
     */
    function autogrow() {
      $element.css({ height: $element[0].scrollHeight + "px" });
    }

    /**
     * Set the selected/active completion item to the one above the current.
     *
     * @return {void}
     */
    function up() {
      const index = $scope.$parent.completions.items.indexOf(
        $scope.$parent.completions.active,
      );

      if (index > 0) {
        $scope.$parent.completions.active =
          $scope.$parent.completions.items[index - 1];
      } else {
        $scope.$parent.completions.active =
          $scope.$parent.completions.items[
            $scope.$parent.completions.items.length - 1
          ];
      }
    }

    /**
     * Set the selected/active completion item to the one below the current.
     *
     * @return {void}
     */
    function down() {
      const index = $scope.$parent.completions.items.indexOf(
        $scope.$parent.completions.active,
      );

      if (index !== -1 && index < $scope.$parent.completions.items.length - 1) {
        $scope.$parent.completions.active =
          $scope.$parent.completions.items[index + 1];
      } else {
        $scope.$parent.completions.active = $scope.$parent.completions.items[0];
      }
    }

    /**
     * Expand the selected item in to a fully blown message entity that exists in the text.
     *
     * @param  {{ data: iForumEntity }} item
     * @return {void}
     */
    function select(item: { data: iForumEntity }): void {
      if (!item) return;

      function replace(text) {
        const search = vm.searchRegex.exec(text);
        let value = vm.highlighters[item.data.entity_type](
          vm.entities.indexOf(item.data),
        );

        try {
          // Make the browser do HTML stuff for us.
          $tempElement.innerHTML = value;
          value = $tempElement.textContent;
        } finally {
          // Clean up.
          $tempElement.innerHTML = null;
        }

        return (
          text.substr(0, search.index + search[0].indexOf("@")) +
          value +
          " " +
          text.substr(search.index + search[0].length)
        );
      }

      const entityIndex = (vm.entities as iForumEntity[]).findIndex(
        (entity) => entity.entity_id === item.data.entity_id,
      );
      if (entityIndex < 0) {
        vm.entities.push(item.data);
      } else {
        vm.entities[entityIndex] = item.data;
      }

      vm.end();
      vm.ngModel.$setViewValue(replace(vm.ngModel.$viewValue));
      vm.ngModel.$render();
    }

    $element.on("keyup click focus", () => {
      if (vm.moved) return (vm.moved = false);
      if (vm.timeout) $timeout.cancel(vm.timeout);

      vm.timeout = $timeout(() => {
        const value = $element.val();
        const match = vm.searchRegex.exec(
          value.substr(0, $element[0].selectionStart),
        );

        if (match) {
          vm.start(match);
        } else {
          vm.end();
        }
      }, 300);

      $timeout(vm.autogrow, 0);

      if (!$scope.$$phase) {
        $scope.$apply();
      }
    });

    $element.on("keydown", (evt: KeyboardEvent) => {
      if (!$scope.$parent.searching) {
        return;
      }

      switch (evt.keyCode) {
        case 13: // enter
          vm.select($scope.$parent.completions.active);
          break;
        case 38: // up
          vm.up();
          break;
        case 40: // down
          vm.down();
          break;
        default:
          return;
      }

      evt.preventDefault();
      vm.moved = true;

      if (!$scope.$$phase) {
        $scope.$apply();
      }
    });

    $timeout(vm.autogrow, 100);
    $timeout(vm.render, 100);
  }

  /**
   * ========= MODULES =========
   */
  forumEditorAllowMentions.$inject = ["$q", "UsersApi"];

  /**
   * Extends functionality in the forum-editor directive to allow people to
   * mention people by their full name. Built to maybe be a toggle-able
   * feature in the future.
   *
   * @return {Object}
   */
  function forumEditorAllowMentions($q, UsersApi) {
    /**
     * Provide functionality to the parent directive. $input refers to the
     * parent directive, or, the forum editor.
     */
    function link($scope, $element, $attributes, $input) {
      const $tempElement = document.createElement("span");

      /**
       * Decode the entity by the index specified.
       *
       * @return {Number}
       */
      function decode(index) {
        if (index instanceof String) {
          // Absolubtley not.
          index = index.replace(/@\\[(\d+)\\]/, "$1");
          index = parseInt(index, 10);
        }

        const entity: iForumEntity = $input.entities[index];

        switch (entity.entity_type) {
          case "user":
            return new UserModel(entity.entity as iUser).entityBody(
              entity.body,
            );
          default:
            return entity.body;
        }
      }

      /**
       * Decode the entity by the index specified.
       *
       * @return {Number}
       */
      function highlight(index) {
        if (index instanceof String) {
          // Absolubtley not.
          index = index.replace(/@\\[(\d+)\\]/, "$1");
          index = parseInt(index, 10);
        }

        if ($input.entities && $input.entities[index]) {
          return $input.entities[index].entity.full_name;
        }
      }

      /**
       * Encode the entity in place of the text.
       *
       * @param {{ data: iForumEntity }}
       * @return {string}
       */
      function encode(entity): string {
        if ($input.entities.indexOf(entity) !== -1) {
          return "@[" + $input.entities.indexOf(entity) + "]";
        }

        return entity;
      }

      /**
       * Called when the parent directive does a search and wants to piece
       * together items which can be included in the forum post. In this
       * instance, get people who can be mentioned in a post.
       *
       * @param  {string} search
       * @return {Promise<void>}
       */
      function completionCallback(search: string): Promise<any[]> {
        return new Promise((resolve, reject) => {
          const data = [];

          UsersApi.get({
            full_name: search,
          }).$promise.then((resp) => {
            resp.data.forEach((item) => {
              data.push({
                image: item.img,
                title: item.full_name,
                subtitle: item.company,
                data: {
                  body: item.full_name,
                  entity_id: item.id,
                  entity_type: "user",
                  entity: item,
                },
              });
            });
            resolve(data);
          }, reject);
        });
      }

      /**
       * Parse the value entered in to the editor input box. Use the event
       * as an opportunity to clean out unused entities and generally
       * clean up the input value.
       *
       * @param  {String} value
       * @return {String}
       */
      function formatterCallback(value: string): string {
        if (!value) value = "";

        // Remove mentions which aren't in the text anymore.
        $input.entities = $input.entities.filter(
          (val: iForumEntity, ind: number) => {
            if (val.entity_type === "user") {
              if (value.indexOf(encode(val)) !== -1) {
                value = value.replace(encode(val), highlight(ind));
                return true;
              }

              return false;
            }

            // None of our business.
            return true;
          },
        );

        $input.render(value);

        return value;
      }

      /**
       * Parse the value entered in to the editor input box. Use the event
       * as an opportunity to clean out unused entities and generally
       * clean up the input value.
       *
       * @param  {String} value
       * @return {String}
       */
      function parserCallback(value) {
        // Remove mentions which aren't in the text anymore.
        $input.entities = $input.entities.filter((val: iForumEntity, ind) => {
          if (val.entity_type === "user") {
            if (value.indexOf(highlight(ind)) !== -1) {
              value = value.replace(highlight(ind), encode(val));
              return true;
            }

            return false;
          }

          // None of our business.
          return true;
        });

        $input.render(value);

        return value;
      }

      /**
       * Register the formatters, parsers and searchers for the parent
       * directive, which it uses to compile the items to allow as
       * entities that can be added to the post, and also allows to
       */
      $input.registerEncoder("user", encode);
      $input.registerDecoder("user", decode);
      $input.registerHighlighter("user", highlight);
      $input.registerCompletion(completionCallback);
      $input.registerFormatter(formatterCallback);
      $input.registerParser(parserCallback);
    }

    return {
      require: "^forumEditorInput",
      restrict: "A",
      link,
    };
  }
})(angular);
